'use client';

import { Link } from '../link';
import styles from './ui.module.css';
import { WORK_NAV, type NavSection, type Workplace } from '@goodbuild/rules/site';

/**
 * The navigation down the side of a work surface.
 *
 * **The bar across the top is the players'.** It carries Games, Publishing,
 * Library and Orders, and it stays exactly as it is: a studio and a creator
 * both reach the store through it, and both are players too. What it cannot do
 * is hold a studio's own screens, because they are not a player's and adding
 * them would give a visitor evaluating the platform a header full of surfaces
 * they cannot open.
 *
 * So a second navigation, vertical, on the audience's own pages only. It holds
 * the screens of one account — the portal, its statistics, its builds, its
 * payouts — and it is drawn in that panel's accent, so the page says whose it
 * is twice over: the ground it is painted on and the map down its left edge.
 *
 * **And the staff panel is a third one since #111**, which is why this takes a
 * `Workplace` rather than a `WorkSite`. Nothing about the markup or the CSS
 * changes for it — the admin screens are a workplace in exactly the sense this
 * component was written for, somebody who will be in it for an hour — and the
 * only thing that had to move is the label read out to a screen reader. See
 * `aria-label` below.
 *
 * **A client component**, and for the same reason `SiteNav` is one: it renders
 * `Link`, which resolves through a context the application installs.
 *
 * `aria-current="page"` rather than only a class. The marker is a colour and a
 * rule, and a colour is not available to somebody using a screen reader — this
 * is the same information said in the way that surface understands.
 */
export function WorkNav({
  site,
  sections,
  currentPath,
}: {
  /**
   * Which set of screens this is a navigation of.
   *
   * A `Workplace`, so `admin` is one of the answers — and deliberately not a
   * `SiteId`, because the staff panel has no hostname and no ground of its own.
   * See `Workplace` in `../site`.
   */
  site: Workplace;
  /**
   * What to draw, when the audience's standing list is not the answer.
   *
   * Defaults to `WORK_NAV[site]`, which is what every surface that has no
   * opinion gets. A caller passes this when the rail depends on something only
   * the application knows — which organisation is being looked at (#88),
   * whether the creator has onboarded at all (#90) — and it arrives through
   * `Page` from `AccountState.rail` rather than being threaded per route.
   *
   * An empty list is a real answer and renders nothing at all; `Page` is where
   * that decision is made, because it owns the two-column grid the rail would
   * otherwise leave an empty column of.
   */
  sections?: readonly NavSection[] | undefined;
  /**
   * The path being viewed, so the entry for it can be marked.
   *
   * Optional, and unmarked when absent: a surface that does not know its own
   * path should draw no marker rather than guess at one and mark the wrong
   * entry, which is worse than marking none.
   */
  currentPath?: string | undefined;
}): React.ReactElement {
  /*
   * **`storeOrigin` was a prop here and is gone.**
   *
   * Every entry in `WORK_NAV` is a path this deployment serves, and so is every
   * path a rail could reasonably grow — the public programme page included, now
   * that the storefront is part of the same bundle. There is no second origin
   * left to resolve against.
   */
  const groups = sections ?? WORK_NAV[site];

  /*
   * `Your account` is true of the two audiences and false of the staff panel,
   * and it is the one string in this component that had to change for #111.
   *
   * An administrator is not looking at their own account here — they are
   * looking at everybody else's: the people table, the studio applications, the
   * ledger the whole platform's money runs through. A screen-reader user
   * tabbing to a landmark announced as "Your account navigation" on
   * `/admin/people` would be told the wrong thing about whose data is on the
   * screen, which on this particular panel is the one thing worth being right
   * about.
   *
   * The visible chrome needs no equivalent, because the page says it: the h1 on
   * every one of these screens names the queue, and the rail is beside it.
   */
  return (
    <nav className={styles.workNav} aria-label={site === 'admin' ? 'Admin' : 'Your account'}>
      {groups.map((section) => (
        <div key={section.title} className={styles.workNavSection}>
          <p className={styles.workNavTitle}>{section.title}</p>
          <ul className={styles.workNavList}>
            {section.items.map((item) => {
              /*
               * Exact match, the same rule the top bar uses. A prefix match
               * would light the first entry up on every page under `/portal`,
               * including the ones with entries of their own — so two things
               * would be marked as the page you are on, which is worse than
               * nothing being marked. The staff panel makes that sharper
               * still (#111): every one of its eleven screens is under
               * `/admin`, so a prefix rule would mark `Overview` as the current
               * page on all of them.
               */
              const here = currentPath === item.href;

              return (
                <li key={item.href}>
                  <Link
                    href={item.href}
                    className={
                      here ? `${styles.workNavLink} ${styles.workNavCurrent}` : styles.workNavLink
                    }
                    {...(here ? { 'aria-current': 'page' as const } : {})}
                  >
                    {item.label}
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </nav>
  );
}
